// src/components/Albums.js
import { useState, useEffect } from 'react';
import SpotifyWebApi from 'spotify-web-api-js';
import Navbar from './Navbar';

const spotifyApi = new SpotifyWebApi();

const Albums = ({ token }) => {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('albums');

  // Fetch albums on component mount
  useEffect(() => {
    const fetchAlbums = async () => {
      if (!token) return;
      try {
        setLoading(true);
        spotifyApi.setAccessToken(token);
        const response = await spotifyApi.getMySavedAlbums({ limit: 50 });
        setAlbums(response.items);
      } catch (err) {
        setError('Failed to fetch albums');
        console.error('Error fetching albums:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAlbums();
  }, [token]);

  // Fetch tracks when an album is selected
  useEffect(() => {
    const fetchTracks = async () => {
      if (!selectedAlbum) return;
      try {
        setLoading(true);
        const response = await spotifyApi.getAlbumTracks(selectedAlbum.album.id);
        setTracks(response.items);
      } catch (err) {
        setError('Failed to fetch tracks');
        console.error('Error fetching tracks:', err);
      } finally {
        setLoading(false);
      }
    };

    if (selectedAlbum) {
      fetchTracks();
    }
  }, [selectedAlbum]);

  const handlePlayAlbum = async (albumUri) => {
    try {
      await spotifyApi.play({
        context_uri: albumUri
      });
    } catch (err) {
      setError('Failed to play album. Make sure you have a Premium account and an active device.');
      console.error('Error playing album:', err);
    }
  };

  const handlePlayTrack = async (trackUri, index) => {
    try {
      await spotifyApi.play({
        context_uri: selectedAlbum.album.uri,
        offset: { position: index }
      });
    } catch (err) {
      setError('Failed to play track. Make sure you have a Premium account and an active device.');
      console.error('Error playing track:', err);
    }
  };

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-9 p-4">
            <div className="d-flex align-items-center">
              <h2 className="me-3">Loading...</h2>
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}
      <div className="row">
        <div className="col-md-9 p-4">

          {viewMode === 'albums' ? (
            <>
              <h2 className="mb-4">Your Albums</h2>
              <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-4">
                {albums.map(({ album }) => (
                  <div key={album.id} className="col">
                    <div className="card h-100 border-0 shadow-sm">
                      {/* Square image container */}
                      <div className="position-relative" style={{ width: '100%', paddingBottom: '100%' }}>
                        {album.images?.[0] ? (
                          <img
                            src={album.images[0].url}
                            alt={album.name}
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{ objectFit: 'contain' }}
                          />
                        ) : (
                          <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark d-flex align-items-center justify-content-center">
                            <i className="bi bi-disc text-light" style={{ fontSize: '2rem' }}></i>
                          </div>
                        )}
                      </div>
                      <div className="card-body">
                        <h5 className="card-title text-truncate mb-1">{album.name}</h5>
                        <p className="card-text">
                          <small className="text-muted">
                            {album.artists.map(artist => artist.name).join(', ')}<br />
                            {album.total_tracks} tracks • {new Date(album.release_date).getFullYear()}
                          </small>
                        </p>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success btn-sm flex-grow-1"
                            onClick={() => handlePlayAlbum(album.uri)}
                          >
                            <i className="bi bi-play-fill"></i> Play
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm flex-grow-1"
                            onClick={() => {
                              setSelectedAlbum({ album });
                              setViewMode('tracks');
                            }}
                          >
                            View Tracks
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div className="d-flex gap-4 align-items-center">
                  {selectedAlbum?.album.images?.[0] && (
                    <img
                      src={selectedAlbum.album.images[0].url}
                      alt={selectedAlbum.album.name}
                      style={{ width: '150px', height: '150px' }}
                      className="rounded shadow-sm"
                    />
                  )}
                  <div>
                    <h2 className="mb-0">{selectedAlbum?.album.name}</h2>
                    <p className="text-muted mb-0">
                      {selectedAlbum?.album.artists.map(artist => artist.name).join(', ')} • 
                      {' '}{new Date(selectedAlbum?.album.release_date).getFullYear()} • 
                      {' '}{selectedAlbum?.album.total_tracks} tracks
                    </p>
                  </div>
                </div>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setViewMode('albums');
                    setSelectedAlbum(null);
                  }}
                >
                  Back to Albums
                </button>
              </div>

              <div className="list-group">
                {tracks.map((track, index) => (
                  <button
                    key={track.id}
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                    onClick={() => handlePlayTrack(track.uri, index)}
                  >
                    <div className="text-muted" style={{ width: '30px' }}>
                      {(index + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="d-flex flex-column flex-grow-1">
                      <strong>{track.name}</strong>
                      {track.artists && (
                        <small className="text-muted">
                          {track.artists.map(artist => artist.name).join(', ')}
                        </small>
                      )}
                    </div>
                    <div className="text-muted" style={{ minWidth: '60px', textAlign: 'right' }}>
                      {formatTime(track.duration_ms)}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};

export default Albums;